import { uniqBy } from "lodash-es";
import { type FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MenuListBox, MenuOptionItem, Popover, TriggerButton, useContextualMenu } from "widgets";
import { Item } from "widgets";

import { getIconElementForPlaybook, loadPlaybooks } from "@/app-utils/playbook.ts";
import type { Playbook } from "@/graphql.ts";
import { updateNfPlaybookExecutionState } from "@/redux/modules/nf-playbook-execution.ts";
import { openPlaybook } from "@/redux/modules/playbook-execution.ts";
import { loadSampleTypeForPlaybooksButton, updateSampleEditingState } from "@/redux/modules/sample-editing.ts";
import {
  getTableDataSlice,
  getTableQuerySpecWithSelection,
  type ITableData,
  type ITableDataState,
} from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { PLAYBOOK_TYPE } from "@/settings/settings.ts";
import { ProtectedResourceLogo } from "@/ui-modules/core/ProtectedResourceLogo.tsx";

import menuStyle from "./styles/ContextualMenu.module.scss";

interface ISamplePlaybooksButtonProps {
  activeTable: ITableData;
}

const SamplePlaybooksButton: FC<ISamplePlaybooksButtonProps> = (props) => {
  const triggerRef = useRef(null);
  const { state, triggerProps, menuProps } = useContextualMenu(triggerRef);

  return (
    <>
      <TriggerButton
        {...triggerProps}
        ref={triggerRef}
        icon={"desktop_windows"}
        label={"Playbooks"}
        id={"button-header-playbooks"}
        showLabel
      />
      <Popover state={state} triggerRef={triggerRef} placement={"bottom start"}>
        <SamplePlaybooksMenuDialog {...menuProps} {...props} />
      </Popover>
    </>
  );
};

const SamplePlaybooksMenuDialog: FC<ISamplePlaybooksButtonProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const [isLoading, setLoading] = useState<boolean>(true);
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [hasMultipleSampleTypes, setMultipleSampleTypes] = useState<boolean>(false);
  const tableDataState = useSelector<IAppState, ITableDataState>((state) => state.tableData);
  const sampleTableQuerySpec = getTableQuerySpecWithSelection(props.activeTable);

  useEffect(() => {
    void (async () => {
      const [samplePlaybooks, sampleEditingData] = await Promise.all([
        dispatch(loadPlaybooks()).then((playbooks) => {
          return playbooks.filter(
            (p) =>
              p.type?.id === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES ||
              p.type?.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES ||
              (p.type?.id === PLAYBOOK_TYPE.PROCESSOR && p.sampleType),
          );
        }),
        dispatch(loadSampleTypeForPlaybooksButton(sampleTableQuerySpec)),
      ]);

      const sampleTypes = uniqBy(
        sampleEditingData?.samples?.map((sample) => sample?.type),
        "id",
      );

      // if multiple sample types are in the selection then no playbook can handle it
      if (sampleTypes?.length === 1) {
        const sampleType = sampleTypes[0];
        const filteredPlaybooks = samplePlaybooks.filter((p) => p.sampleType?.id === sampleType?.id);
        setPlaybooks(filteredPlaybooks);
        setMultipleSampleTypes(false);
      } else {
        setMultipleSampleTypes(true);
      }

      setLoading(false);
    })();
  }, [props.activeTable]);

  const getPlaybooksMenuMessage = () => {
    if (isLoading) {
      return "Loading compatible playbooks...";
    } else if (hasMultipleSampleTypes) {
      return "To run a Playbook you must select samples that have a common sample type.";
    } else if (playbooks.length === 0) {
      return "There are no Playbooks that are compatible with your current selection.";
    } else {
      return "Selected samples will be used for analysis.";
    }
  };

  return (
    <MenuListBox
      {...props}
      isLoading={isLoading}
      footNote={
        <div className={menuStyle.foot_note} style={{ minWidth: 380, maxWidth: "unset" }}>
          {getPlaybooksMenuMessage()}
        </div>
      }
      onAction={(key) => {
        const playbook = playbooks.find((p) => String(p.id) === key);
        if (playbook.type.id === PLAYBOOK_TYPE.PROCESSOR) {
          const tableData = getTableDataSlice(tableDataState, sampleTableQuerySpec);
          const selectedSampleIds = tableData.selectionModel?.selectedIds as number[];

          dispatch(
            updateNfPlaybookExecutionState({
              sampleIds: selectedSampleIds.length > 0 ? selectedSampleIds : tableData.rowIds,
            }),
          );
        } else {
          dispatch(updateSampleEditingState({ sampleEditingTableQuerySpec: sampleTableQuerySpec }));
        }
        dispatch(openPlaybook(playbook, true));
      }}
    >
      {playbooks?.map((playbook) => (
        <Item key={String(playbook.id)}>
          <MenuOptionItem
            title={playbook.nameSuffix != undefined ? `${playbook.name} : ${playbook.nameSuffix}` : playbook.name}
            description={playbook.description}
            icon={<img width={50} src={getIconElementForPlaybook(playbook).large} alt={"Playbook icon"} />}
            tag={playbook.standard ? <ProtectedResourceLogo size={16} /> : undefined}
          />
        </Item>
      ))}
    </MenuListBox>
  );
};

export default SamplePlaybooksButton;
